import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  importsAPI,
  type WritebackRow,
  type WritebackStatusResponse,
} from '../../lib/api/imports';
import { WritebackTable, STALE_PENDING_HOURS, type Outcome } from './ImportWritebackTable';


const WINDOW_OPTIONS = [
  { label: 'Last 24 hours', value: 24 },
  { label: 'Last 48 hours', value: 48 },
  { label: 'Last 7 days', value: 168 },
];

/**
 * Writeback queue for one customer: what is still waiting to reach the ERP,
 * and what succeeded or failed inside the chosen window.
 *
 * Pending is deliberately not windowed. A writeback stuck for a week is the
 * thing this tab exists to surface, and a 48-hour cut would hide exactly that.
 *
 * @param customerId - Customer whose writebacks to show
 */
export default function ImportWritebackTab({ customerId }: { customerId: number }) {
  const [data, setData] = useState<WritebackStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [hours, setHours] = useState(48);
  const [outcome, setOutcome] = useState<Outcome>('pending');
  const [cancelling, setCancelling] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (!customerId) { setLoading(false); return; }
    try {
      if (isRefresh) setRefreshing(true); else setLoading(true);
      setData(await importsAPI.getWritebackStatus(customerId, hours));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load writeback status';
      toast.error(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [customerId, hours]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Ids are only meaningful against the list they came from.
  useEffect(() => { setSelected(new Set()); }, [outcome, hours, customerId]);

  const toggle = useCallback((id: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((ids: number[]) => {
    setSelected(prev => (ids.every(id => prev.has(id)) ? new Set() : new Set(ids)));
  }, []);

  const cancelSelected = useCallback(async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(
      `Cancel ${ids.length} writeback${ids.length === 1 ? '' : 's'}?\n\n`
      + 'They will not be sent to the ERP, and the import will stop holding '
      + 'those fields — the ERP\'s values will apply again on the next import.\n\n'
      + 'The records are kept, marked Cancelled.',
    )) return;
    setBulkRunning(true);
    try {
      const res = await importsAPI.cancelWritebacks(customerId, { ids });
      toast.success(`${res.cancelled} writeback${res.cancelled === 1 ? '' : 's'} cancelled`);
      setSelected(new Set());
      await fetchData(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not cancel');
    } finally {
      setBulkRunning(false);
    }
  }, [selected, customerId, fetchData]);

  const cancelOne = useCallback(async (row: WritebackRow) => {
    // Spelled out because cancelling is not only "stop sending this": while a
    // writeback is pending the import refuses the ERP's value for that field,
    // so withdrawing it hands the field back to the ERP.
    const label = `${row.po_number} / ${row.line_number}`;
    if (!window.confirm(
      `Cancel the writeback for ${label}?\n\n`
      + 'It will not be sent to the ERP, and the import will stop holding '
      + 'that field — the ERP\'s value will apply again on the next import.\n\n'
      + 'The record is kept, marked Cancelled.',
    )) return;
    setCancelling(row.id);
    try {
      await importsAPI.cancelWritebacks(customerId, { ids: [row.id] });
      toast.success(`Writeback for ${label} cancelled`);
      await fetchData(true);
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Could not cancel');
    } finally {
      setCancelling(null);
    }
  }, [customerId, fetchData]);

  if (!customerId) {
    return (
      <Card>
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          Select a customer to view their writeback queue.
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const { summary } = data;
  const oldest = summary.oldest_pending_hours;
  const stalled = oldest !== null && oldest >= STALE_PENDING_HOURS;

  const cards = [
    {
      key: 'pending' as Outcome,
      label: 'Pending',
      value: summary.pending,
      sub: oldest === null ? 'Nothing waiting'
        : oldest < 1 ? `Oldest ${Math.round(oldest * 60)}m`
        : `Oldest ${oldest < 24 ? `${oldest.toFixed(1)}h` : `${Math.floor(oldest / 24)}d`}`,
      icon: Clock,
      color: stalled ? 'text-red-600' : summary.pending > 0 ? 'text-amber-600' : 'text-emerald-600',
      bg: stalled ? 'bg-red-50' : summary.pending > 0 ? 'bg-amber-50' : 'bg-emerald-50',
    },
    {
      key: 'succeeded' as Outcome,
      label: 'Written back',
      value: summary.succeeded,
      sub: `Last ${hours}h`,
      icon: CheckCircle2,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    {
      key: 'failed' as Outcome,
      label: 'Failed',
      value: summary.failed,
      sub: `Last ${hours}h`,
      icon: XCircle,
      color: summary.failed > 0 ? 'text-red-600' : 'text-emerald-600',
      bg: summary.failed > 0 ? 'bg-red-50' : 'bg-emerald-50',
    },
    {
      key: 'cancelled' as Outcome,
      label: 'Cancelled',
      value: summary.cancelled ?? 0,
      sub: `Last ${hours}h`,
      icon: Ban,
      color: 'text-slate-600',
      bg: 'bg-slate-100',
    },
  ];

  return (
    <div className="space-y-6">
      {/* A stalled queue is not just a backlog: while a writeback is pending,
          the import holds that field against the ERP feed, so this banner is
          also telling the reader which fields are currently frozen. */}
      {stalled && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Writeback queue looks stalled</p>
            <p className="text-xs text-red-700 mt-0.5">
              The oldest pending writeback has been waiting{' '}
              {oldest !== null && oldest < 24 ? `${oldest.toFixed(1)} hours` : `${Math.floor((oldest ?? 0) / 24)} days`}.
              Until these land, the imports keep holding those fields against the ERP feed.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-1 min-w-[320px]">
          {cards.map(c => (
            <Card
              key={c.key}
              className={`cursor-pointer transition-colors ${outcome === c.key ? 'ring-2 ring-primary' : 'hover:bg-muted/40'}`}
              onClick={() => setOutcome(c.key)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{c.label}</p>
                    <p className="text-2xl font-bold mt-1 tabular-nums">{c.value.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>
                  </div>
                  <div className={`rounded-lg p-2.5 ${c.bg}`}>
                    <c.icon className={`h-5 w-5 ${c.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <select
            id="writeback-window"
            name="window_hours"
            aria-label="Time window"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="px-3 py-2 text-sm font-medium border border-border rounded-lg bg-background shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
          >
            {WINDOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={() => fetchData(true)} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            {outcome === 'pending' ? 'Waiting to be written back'
              : outcome === 'succeeded' ? `Written back successfully (last ${hours}h)`
              : outcome === 'cancelled' ? `Cancelled (last ${hours}h)`
              : `Failed (last ${hours}h)`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {outcome === 'pending' && data.pending.length > 0 && (
            <div className="flex items-center gap-3 mb-3 pb-3 border-b">
              <span className="text-sm text-muted-foreground">
                {selected.size === 0
                  ? 'None selected'
                  : `${selected.size} selected`}
              </span>
              <Button
                variant="outline" size="sm"
                className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                disabled={selected.size === 0 || bulkRunning}
                onClick={() => void cancelSelected()}
              >
                {bulkRunning
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Cancelling…</>
                  : <><Ban className="h-3.5 w-3.5 mr-1.5" />Cancel selected</>}
              </Button>
              {data.truncated.pending && (
                <span className="text-xs text-muted-foreground">
                  Select-all covers the {data.pending.length} rows shown, not all {summary.pending}.
                </span>
              )}
            </div>
          )}
          <WritebackTable
            rows={data[outcome]}
            outcome={outcome}
            truncated={data.truncated[outcome]}
            onCancel={outcome === 'pending' ? cancelOne : undefined}
            cancelling={cancelling}
            selected={outcome === 'pending' ? selected : undefined}
            onToggle={outcome === 'pending' ? toggle : undefined}
            onToggleAll={outcome === 'pending' ? toggleAll : undefined}
            showSapRow={data.show_sap_row ?? false}
          />
        </CardContent>
      </Card>
    </div>
  );
}
